'use client';

import { useEffect, useState } from 'react';

/**
 * CohortCounter — small inline widget showing Founding 100 slots remaining
 * for one tier. Polls /api/cohort/status every 60s. Backend caches the
 * underlying COUNT for the same window, so polling is cheap.
 *
 * Renders three states:
 *   - loading       → "Loading…" (subtle, low-emphasis)
 *   - has remaining → "{n} of 100 Founding slots"
 *   - sold out      → "Founding cohort filled · join at intro pricing"
 *
 * Spec: docs/superpowers/specs/2026-05-14-founding-cohort-and-promos-design.md
 */

type TierId = 'solo' | 'pro' | 'team';

interface TierStatus {
  remaining: number;
  total: number;
  sold_out: boolean;
}

interface CohortStatusResponse {
  solo: TierStatus;
  pro: TierStatus;
  team: TierStatus;
}

const POLL_MS = 60_000;

export function CohortCounter({ tier }: { tier: TierId }) {
  const [status, setStatus] = useState<TierStatus | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const res = await fetch('/api/cohort/status', { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as CohortStatusResponse;
        if (!cancelled) {
          setStatus(data[tier]);
          setError(false);
        }
      } catch {
        // Don't blow up the marketing page over a counter fetch failure.
        // Just suppress — counter quietly disappears, page is still usable.
        if (!cancelled) setError(true);
      }
    }

    void fetchStatus();
    const interval = setInterval(() => void fetchStatus(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [tier]);

  if (error || !status) {
    // Render nothing while loading or on error. The Founding lock label
    // already sits above this in the card; an empty/loading counter line
    // adds noise. Better to surface only confirmed data.
    return null;
  }

  if (status.sold_out) {
    return (
      <p className="text-[10px] text-on-surface-muted mt-1">
        Founding {status.total} filled · joining now is intro pricing only (not locked for life)
      </p>
    );
  }

  return (
    <p className="text-[10px] font-semibold text-primary mt-1">
      {status.remaining} of {status.total} Founding slots remaining
    </p>
  );
}
